import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../../lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import TeamMembersList from './team/TeamMembersList';
import DeletionRequestsCard from './team/DeletionRequestsCard';
import TeamInviteModal from './team/TeamInviteModal';
import TeamEditModal from './team/TeamEditModal';
import TeamResetPasswordModal from './team/TeamResetPasswordModal';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import StatTile from '../../../components/ui/StatTile';
import { isAdmin } from '../../../lib/roles';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'technician', label: 'Technician' },
];

const defaultInviteValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'technician',
  mustChangePassword: true,
};

const defaultEditValues = {
  firstName: '',
  lastName: '',
  email: '',
  role: 'technician',
  mustChangePassword: false,
};

function validatePassword(value) {
  const pw = String(value || '');
  if (pw.length < 12) return 'Password must be at least 12 characters';
  if (!/[A-Z]/.test(pw)) return 'Password must include at least one uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Password must include at least one lowercase letter';
  if (!/[0-9]/.test(pw)) return 'Password must include at least one number';
  return null;
}

export default function TeamSettings() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: defaultInviteValues,
  });

  const editForm = useForm({ defaultValues: defaultEditValues });
  const { register: registerEdit, handleSubmit: handleEditSubmit, reset: resetEdit } = editForm;

  useEffect(() => {
    if (user !== null && !isAdmin(user)) {
      navigate('/settings/profile', { replace: true });
    }
  }, [user, navigate]);

  const { data: users = [], isLoading, error: usersError } = useQuery(
    ['org-users'],
    () => api.get('/organizations/users').then((res) => res.data),
    { enabled: !!user?.organizationId && isAdmin(user) }
  );

  const { data: deletionRequests = [], error: deletionRequestsError } = useQuery(
    ['user-deletion-requests'],
    () => api.get('/users/deletion-requests').then((res) => res.data),
    { enabled: !!user?.organizationId && isAdmin(user) }
  );

  const kpis = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => (u.role || '').toLowerCase() === 'admin').length;
    const pending = users.filter((u) => u.mustChangePassword).length;
    return { total, admins, pending };
  }, [users]);

  const inviteMutation = useMutation(
    (data) =>
      api.post('/organizations/invite', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        role: data.role || 'technician',
        mustChangePassword: data.mustChangePassword !== false,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['org-users']);
        toast.success('Team member invited');
        setShowInviteModal(false);
        reset(defaultInviteValues);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to invite user');
      },
    }
  );

  const onInviteSubmit = (data) => {
    const passwordError = validatePassword(data.password);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    inviteMutation.mutate(data);
  };

  const deleteUserMutation = useMutation(
    (userId) => api.delete(`/organizations/users/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['org-users']);
        toast.success('Team member removed');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to remove user');
      },
    }
  );

  const updateUserMutation = useMutation(
    ({ userId, ...data }) => api.put(`/organizations/users/${userId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['org-users']);
        toast.success('Team member updated');
        setEditingUser(null);
        resetEdit(defaultEditValues);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to update user');
      },
    }
  );

  const handleEditUser = (u) => {
    setEditingUser(u);
    resetEdit({
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      email: u.email || '',
      role: u.role || 'technician',
      mustChangePassword: !!u.mustChangePassword,
    });
  };

  const onEditSubmit = (data) => {
    if (!editingUser) return;
    const newEmail = (data.email || '').trim().toLowerCase();
    const oldEmail = (editingUser.email || '').trim().toLowerCase();
    if (newEmail && newEmail !== oldEmail) {
      const name = [editingUser.firstName, editingUser.lastName].filter(Boolean).join(' ')
        || editingUser.email
        || 'this user';
      const confirmed = window.confirm(
        `This changes ${name}'s login email from ${editingUser.email} to ${data.email.trim()}. They'll need to use the new email to sign in. Continue?`
      );
      if (!confirmed) return;
    }
    updateUserMutation.mutate({
      userId: editingUser.id,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      role: data.role,
      mustChangePassword: !!data.mustChangePassword,
    });
  };

  const resetPasswordMutation = useMutation(
    ({ userId, newPassword }) => api.put(`/users/${userId}/password`, { newPassword, forceChange: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['org-users']);
        toast.success('Password reset. User must change password on next login.');
        setResetPasswordUser(null);
        setResetPasswordValue('');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to reset password');
      },
    }
  );

  const updateDeletionRequestMutation = useMutation(
    ({ id, status }) => api.patch(`/users/deletion-requests/${id}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-deletion-requests']);
        toast.success('Deletion request updated');
      },
      onError: (err) => {
        toast.error(err?.response?.data?.error || 'Failed to update deletion request');
      },
    }
  );

  const handleResetPasswordSubmit = (e) => {
    e.preventDefault();
    if (!resetPasswordUser) return;
    const passwordError = validatePassword(resetPasswordValue);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    resetPasswordMutation.mutate({ userId: resetPasswordUser.id, newPassword: resetPasswordValue });
  };

  const handleDeleteUser = (u) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || 'this user';
    if (!window.confirm(`Are you sure you want to remove ${name} from the team? This action cannot be undone.`)) return;
    deleteUserMutation.mutate(u.id);
  };

  const openInviteModal = () => {
    reset(defaultInviteValues);
    setShowInviteModal(true);
  };

  const closeInviteModal = () => {
    if (!inviteMutation.isLoading) {
      setShowInviteModal(false);
    }
  };

  if (user !== null && !isAdmin(user)) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="page team-page">
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>Loading team…</p>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="page team-page">
        <header className="settings-tab-header settings-tab-header--split">
          <h1 className="settings-tab-title">Team</h1>
        </header>
        <p className="text-muted" style={{ padding: '2rem', textAlign: 'center' }}>Failed to load team.</p>
      </div>
    );
  }

  return (
    <div className="page team-page">
      <header className="settings-tab-header settings-tab-header--split">
        <h1 className="settings-tab-title">Team</h1>
        <div className="settings-tab-header-actions">
          {isAdmin(user) && (
            <Button
              type="button"
              variant="primary"
              className="team-btn-invite"
              onClick={openInviteModal}
            >
              Invite Team Member
            </Button>
          )}
        </div>
      </header>

      <div className="settings-stat-row">
        <Card><StatTile value={kpis.total} label="Total members" /></Card>
        <Card><StatTile value={kpis.admins} label="Admins" /></Card>
        <Card><StatTile value={kpis.pending} label="Pending setup" tone="warning" /></Card>
      </div>

      <TeamMembersList
        users={users}
        isAdminUser={isAdmin(user)}
        currentUserId={user?.id}
        handleEditUser={handleEditUser}
        handleDeleteUser={handleDeleteUser}
        isDeleting={deleteUserMutation.isLoading}
        setResetPasswordUser={setResetPasswordUser}
        setResetPasswordValue={setResetPasswordValue}
      />

      {isAdmin(user) && (
        <DeletionRequestsCard
          deletionRequests={deletionRequests}
          deletionRequestsError={deletionRequestsError}
          updateDeletionRequestMutation={updateDeletionRequestMutation}
        />
      )}

      {showInviteModal && (
        <TeamInviteModal
          onClose={closeInviteModal}
          register={register}
          handleSubmit={handleSubmit}
          onSubmit={onInviteSubmit}
          errors={errors}
          inviteMutation={inviteMutation}
          validatePassword={validatePassword}
          roleOptions={ROLE_OPTIONS}
        />
      )}

      {editingUser && (
        <TeamEditModal
          onClose={() => !updateUserMutation.isLoading && setEditingUser(null)}
          registerEdit={registerEdit}
          handleEditSubmit={handleEditSubmit}
          onEditSubmit={onEditSubmit}
          updateUserMutation={updateUserMutation}
          roleOptions={ROLE_OPTIONS}
        />
      )}

      {resetPasswordUser && (
        <TeamResetPasswordModal
          resetPasswordUser={resetPasswordUser}
          resetPasswordValue={resetPasswordValue}
          setResetPasswordValue={setResetPasswordValue}
          onClose={() => { setResetPasswordUser(null); setResetPasswordValue(''); }}
          onSubmit={handleResetPasswordSubmit}
          resetPasswordMutation={resetPasswordMutation}
        />
      )}
    </div>
  );
}
